import { ArrowLeftIcon, ArrowRightIcon } from '@heroicons/react/24/outline'
import { Datepicker } from 'headless-datetimepicker'
import PropTypes from 'prop-types'
import React from 'react'
import { twMerge as mergeClassNames } from 'tailwind-merge'

/**
 * YearPaginationHeader
 *
 * A component that renders the pagination header for years.
 *
 * @param {number} year - The current year.
 * @returns {JSX.Element} - The rendered component.
 */
const YearPaginationHeader = ({ year, enabledYears }) => {
  const isPrevEnabled = enabledYears.includes(year - 1)
  const isNextEnabled = enabledYears.includes(year + 1)

  return (
    <div className="mb-2 flex items-center justify-between">
      <Datepicker.Button
        action="prev"
        disabled={!isPrevEnabled}
        aria-disabled={!isPrevEnabled}
        className={mergeClassNames(
          'rounded px-2 py-1 text-lg font-medium',
          isPrevEnabled ? 'hover:bg-gray-100' : 'cursor-not-allowed text-gray-300 opacity-25',
        )}
      >
        <ArrowLeftIcon className="h-4 stroke-blue-800 group-hover:stroke-white" />
      </Datepicker.Button>

      <Datepicker.Button
        action="toggleYear"
        className="px-2 py-1 text-lg font-semibold hover:bg-gray-100"
      >
        {year}
      </Datepicker.Button>

      <Datepicker.Button
        action="next"
        disabled={!isNextEnabled}
        aria-disabled={!isNextEnabled}
        className={mergeClassNames(
          'rounded px-2 py-1 text-lg font-medium',
          isNextEnabled ? 'hover:bg-gray-100' : 'cursor-not-allowed text-gray-300 opacity-25',
        )}
      >
        <ArrowRightIcon className="h-4 stroke-blue-800 group-hover:stroke-white" />
      </Datepicker.Button>
    </div>
  )
}

YearPaginationHeader.propTypes = {
  year: PropTypes.number.isRequired,
  enabledYears: PropTypes.arrayOf(PropTypes.number).isRequired,
}

/**
 * BillingPeriodPicker
 *
 * A component wrapper for headless-datetimepicker, that allows the user to select a billing period.
 * consisting of a month and year.
 *
 * @param {boolean} showToday - Whether to show the "Today" option in the picker.
 * @returns {JSX.Element} - The rendered component.
 */
const BillingPeriodPicker = ({ periods, selected, onChange, showToday = false }) => {
  const annualizedPeriods = periods.reduce((acc, period) => {
    const { year } = period
    if (!acc[year]) acc[year] = []
    acc[year].push(period)
    return acc
  }, {})

  return (
    <Datepicker value={undefined}>
      <Datepicker.Picker defaultType="month" alwaysOpen className="w-full rounded-md bg-white p-4">
        {({ year }) => (
          <>
            <YearPaginationHeader
              year={year}
              enabledYears={Object.keys(annualizedPeriods).map(Number)}
            />

            {/* Month Grid */}
            <Datepicker.Items
              type="month"
              className={mergeClassNames(
                'grid w-full auto-rows-max grid-cols-3 gap-1 overflow-y-auto scroll-smooth',
                !showToday && 'mb-2',
              )}
            >
              {({ items }) =>
                items.map((item) => {
                  // Patch the item to add the `isDisabled` property based on available periods
                  const patchedItem = {
                    ...item,
                    isDisabled: !annualizedPeriods[year]?.some((p) => p.month === item.value),
                  }

                  // Find the matching period
                  const matchingPeriod = annualizedPeriods[year]?.find(
                    (p) => p.month === item.value,
                  )

                  return (
                    <Datepicker.Item
                      key={item.key}
                      item={patchedItem}
                      className={mergeClassNames(
                        'grid select-none items-center justify-center rounded-md border-2 border-gray-500/25 py-2 text-sm font-medium hover:bg-[#EEEEEE]',
                        matchingPeriod?.id === selected?.id &&
                          'bg-blue-800 text-white hover:bg-blue-900',
                        patchedItem.isDisabled &&
                          'text-gray pointer-events-none cursor-not-allowed bg-white opacity-25',
                      )}
                      onClick={() => {
                        if (!patchedItem.isDisabled) onChange?.(matchingPeriod)
                      }}
                    >
                      {patchedItem.text.substring(0, 3)}
                    </Datepicker.Item>
                  )
                })
              }
            </Datepicker.Items>

            {showToday && (
              <Datepicker.Button
                action="today"
                className="mt-4 w-full bg-blue-800 p-2 text-sm font-medium text-white hover:bg-blue-900"
              >
                Today
              </Datepicker.Button>
            )}
          </>
        )}
      </Datepicker.Picker>
    </Datepicker>
  )
}

BillingPeriodPicker.propTypes = {
  periods: PropTypes.arrayOf(PropTypes.object),
  selected: PropTypes.object,
  onChange: PropTypes.func,
  showToday: PropTypes.bool,
}

export default BillingPeriodPicker
